import { supabase } from '@/integrations/supabase/client';
import { CommentSortOption } from '../types';

/**
 * Helper function to get date filter for comment queries
 */
export const getDateFilter = (filterBy: string) => {
  const now = new Date();
  
  switch (filterBy) {
    case 'past_day':
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      return yesterday.toISOString();
      
    case 'past_week':
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      return lastWeek.toISOString();
      
    case 'past_month':
      const lastMonth = new Date(now);
      lastMonth.setMonth(now.getMonth() - 1);
      return lastMonth.toISOString();
      
    default:
      return null;
  }
};

/**
 * Helper function to safely process comment data
 */
export const safeProcessComment = (rawComment: any) => {
  // Create a safe default profile
  const safeProfile = {
    id: rawComment.user_id || null,
    name: 'Anonymous User',
    avatar_url: null
  };
  
  // Check if profile exists and has the expected structure
  if (rawComment.profile && typeof rawComment.profile === 'object') {
    safeProfile.name = rawComment.profile.name || 'Anonymous User';
    safeProfile.avatar_url = rawComment.profile.avatar_url || null;
    safeProfile.id = rawComment.profile.id || rawComment.user_id || null;
  }
  
  // Return a properly shaped Comment object
  return {
    id: rawComment.id,
    user_id: rawComment.user_id,
    anonymous_id: rawComment.anonymous_id,
    content: rawComment.content,
    parent_id: rawComment.parent_id,
    created_at: rawComment.created_at,
    updated_at: rawComment.updated_at,
    deleted: rawComment.deleted || false,
    reported: rawComment.reported || false,
    profile: safeProfile,
    // Add conditional fields based on the comment type
    ...(rawComment.poll_id ? { poll_id: rawComment.poll_id } : {}),
    ...(rawComment.card_id ? { card_id: rawComment.card_id } : {})
  };
};

/**
 * Fetch profile for a comment
 */
export const fetchProfileForComment = async (userId: string | null) => {
  if (!userId) {
    return {
      id: null,
      name: 'Anonymous User',
      avatar_url: null
    };
  }
  
  try {
    const { data } = await supabase
      .from('profiles')
      .select('id, name')
      .eq('id', userId)
      .single();
      
    if (!data) {
      return {
        id: userId,
        name: 'User',
        avatar_url: null
      };
    }
    
    return {
      id: data.id,
      name: data.name || 'User',
      avatar_url: null
    };
  } catch (error) {
    console.error('Error fetching profile:', error);
    return {
      id: userId,
      name: 'User',
      avatar_url: null
    };
  }
};

/**
 * Generate query with proper sorting applied
 */
export const applySorting = (query: any, sortBy: CommentSortOption) => {
  switch (sortBy) {
    case 'newest':
      return query.order('created_at', { ascending: false });
    case 'oldest':
      return query.order('created_at', { ascending: true });
    case 'top':
      return query.order('created_at', { ascending: false });
    default:
      return query.order('created_at', { ascending: false });
  }
};
